import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, BrowserRouter as Router } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Amplify, Auth } from "aws-amplify";
import awsExports from "./aws-exports";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { routesadmin, routesuser, defaultroute, routesorganizer } from "routes";
import { useSoftUIController, setMiniSidenav } from "context";
import brand from "assets/images/logo-ct.png";
import { SnackbarProvider } from 'notistack';
import EditEventPage from 'layouts/event/pages/editevent'; // Import the EditEventPage

Amplify.configure(awsExports);

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [routes, setRoutes] = useState(routesuser);
  const [tenant, setTenant] = useState("");
  const [email, setEmail] = useState("");
  const [authenticated, setAuthenticated] = useState(false); // Track authentication status
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    Auth.currentUserInfo().then((value) => {
      if (value && value.attributes) {
        setTenant(value.attributes["custom:company"]);
        setEmail(value.attributes["email"]);
        const accStatus = value.attributes["custom:accStatus"];
        setAuthenticated(true); // Set authenticated to true
        if (value.username !== "") {
          if (accStatus === "Admin") {
            setRoutes(routesadmin);
          } else if (accStatus === "organizer") {
            setRoutes(routesorganizer);
          } else if (accStatus === "user") {
            setRoutes(routesuser);
          }
        }
      } else {
        setAuthenticated(false); // Set authenticated to false if no user info
      }
    }).catch(() => {
      setAuthenticated(false); // Set authenticated to false if error
    });
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route, index) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.name !== "Edit Event") { // Exclude Edit Event from navigation
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key || index} // Use route.key or index as key
          />
        );
      }

      return null;
    });

  return (
    <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {authenticated && layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={brand}
              brandName={tenant}
              email={email}
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
          </>
        )}
        {authenticated && layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route
            path="/events/:eventId"
            element={<EditEventPage />}
            key="events" // Add a unique key for Edit Event route
          />
          {!authenticated && (
            <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
          )}
        </Routes>
      </ThemeProvider>
    </SnackbarProvider>
  );
}
